
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  videoUrl: string;
}

export const VideoModal = ({ isOpen, onClose, title, videoUrl }: VideoModalProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [errorLoading, setErrorLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isVimeo, setIsVimeo] = useState(false);

  useEffect(() => {
    // Reset states when the modal opens with a new video
    if (isOpen) {
      setErrorLoading(false);
      setIsLoading(true);
      // Check if it's a Vimeo URL
      setIsVimeo(videoUrl.includes('vimeo.com') || videoUrl.includes('player.vimeo.com'));
    }
  }, [isOpen, videoUrl]);

  useEffect(() => {
    // Clean up function to stop video when modal closes
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src'); 
        videoRef.current.load();
      }
    };
  }, []);

  const handleVideoError = () => {
    console.error("Video error event triggered for:", videoUrl);
    setErrorLoading(true);
    setIsLoading(false);
  };

  const handleVideoLoaded = () => {
    console.log("Video loaded successfully:", videoUrl);
    setIsLoading(false);
  };

  const handleIframeLoad = () => {
    console.log("Iframe loaded successfully:", videoUrl);
    setIsLoading(false);
  };

  // Process Vimeo URL to ensure it has proper parameters
  const processVimeoUrl = (url: string) => {
    // Make sure URL ends with proper embed parameters
    if (url.includes('player.vimeo.com')) {
      // Add parameters if they don't exist
      const hasParams = url.includes('?');
      return `${url}${hasParams ? '&' : '?'}autoplay=1&title=0&byline=0&portrait=0`;
    }
    return url;
  };
  
  // Get the effective video URL (processed if Vimeo)
  const effectiveVideoUrl = isVimeo ? processVimeoUrl(videoUrl) : videoUrl;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {errorLoading 
              ? "Video could not be loaded. Please try again later." 
              : "Click outside to close this video"}
          </DialogDescription>
        </DialogHeader>
        <div className="aspect-video w-full">
          {isLoading && !errorLoading && (
            <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">Loading video...</p>
            </div>
          )}
          
          {isVimeo ? (
            <iframe
              ref={iframeRef}
              src={effectiveVideoUrl}
              className={`w-full h-full rounded-lg ${isLoading ? 'hidden' : ''}`}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              onLoad={handleIframeLoad}
              onError={handleVideoError}
            ></iframe>
          ) : (
            <video
              ref={videoRef}
              className={`w-full h-full rounded-lg ${isLoading && !errorLoading ? 'hidden' : ''}`}
              controls
              playsInline
              preload="metadata"
              onError={handleVideoError}
              onLoadedData={handleVideoLoaded}
              src={effectiveVideoUrl}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
